/**
 * Procedural placeholder tug (aircraft tow tractor).
 *
 * A throwaway low-poly stand-in until the real GLB tug models are generated.
 * Built directly in METERS (like the procedural aircraft) with the FRONT facing
 * +Z, wheels resting on y = 0, and origin centered on the footprint — so the
 * same heading math and `updateModelTransform` used for aircraft apply unchanged.
 *
 * Swap-in path: once a GLB exists, render it through the normal aircraft loader
 * with `modelSettings` (filePath, scale, rotation, headingAxis) instead of this.
 */
import * as THREE from 'three'

// Per-instance materials. NOT shared singletons: the layer service disposes an
// object's materials on removal, so sharing would destroy other live tugs.
const makeBodyMat = () => new THREE.MeshStandardMaterial({ color: 0xf2b500, roughness: 0.5, metalness: 0.1 })
const makeDarkMat = () => new THREE.MeshStandardMaterial({ color: 0x2b2b2b, roughness: 0.7, metalness: 0.2 })
const makeBeaconMat = () =>
  new THREE.MeshStandardMaterial({ color: 0xffb020, emissive: 0xff7a00, emissiveIntensity: 0.6, roughness: 0.4 })

// Dimensions in meters (a compact towbar ramp tractor).
const BODY_L = 3.4
const BODY_W = 1.7
const BODY_H = 0.9
const WHEEL_R = 0.34
const WHEEL_W = 0.28
const TOWBAR_L = 2.2

export class ProceduralTugFactory {
  /** Build a tug group. Front = +Z, wheels at y = 0, centered on X/Z. */
  static create(): THREE.Group {
    const g = new THREE.Group()
    const bodyMat = makeBodyMat()
    const darkMat = makeDarkMat()
    const beaconMat = makeBeaconMat()

    // Lower chassis + upper hood (front, +Z) + cab (rear, −Z).
    const chassis = new THREE.Mesh(new THREE.BoxGeometry(BODY_W, BODY_H, BODY_L), bodyMat)
    chassis.position.set(0, WHEEL_R + BODY_H / 2, 0)
    g.add(chassis)

    const cab = new THREE.Mesh(new THREE.BoxGeometry(BODY_W * 0.82, BODY_H * 0.8, BODY_L * 0.42), darkMat)
    cab.position.set(0, WHEEL_R + BODY_H + BODY_H * 0.4 - 0.05, -BODY_L * 0.22)
    g.add(cab)

    // Rotating beacon on the cab roof.
    const beacon = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 0.16, 8), beaconMat)
    beacon.position.set(0, WHEEL_R + BODY_H + BODY_H * 0.8 + 0.05, -BODY_L * 0.22)
    g.add(beacon)

    // Four wheels (cylinders laid on the X axis). One geometry per tug instance.
    const wheelGeo = new THREE.CylinderGeometry(WHEEL_R, WHEEL_R, WHEEL_W, 12)
    const halfW = BODY_W / 2
    const axleZ = BODY_L * 0.32
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        const w = new THREE.Mesh(wheelGeo, darkMat)
        w.rotation.z = Math.PI / 2
        w.position.set(sx * halfW, WHEEL_R, sz * axleZ)
        g.add(w)
      }
    }

    // Towbar reaching out the front (+Z) toward the aircraft nose gear.
    const bar = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.14, TOWBAR_L), darkMat)
    bar.position.set(0, WHEEL_R + 0.15, BODY_L / 2 + TOWBAR_L / 2)
    g.add(bar)

    g.traverse((c) => {
      if (c instanceof THREE.Mesh) {
        c.castShadow = true
        c.receiveShadow = true
      }
    })

    // Transform base so updateModelTransform() places it on the ground (no extra
    // clearance — wheels already model y = 0) and applies heading on Y.
    g.userData.transformBase = {
      kind: 'procedural',
      yOffsetFeet: 0,
      baseRot: [0, 0, 0] as [number, number, number],
      headingAxis: 'y' as const,
      proceduralGroundY: 0,
    }

    return g
  }
}
